// PermissionContext - Gestion des permissions et feature flags
// Source unique de vérité pour l'autorisation côté frontend
// Utilise /api/platform/context qui est calculé par le même moteur que le backend

import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { featureAccordee, featureExacte } from "../utils/droitsMenu";
import { verdictBureau } from "../pages/omnihub/accueilBureau";

const API = process.env.REACT_APP_BACKEND_URL || "";

const PermissionContext = createContext(null);

// 🔑 APERÇU « voir comme ce tenant » — clé de persistance locale.
// ⚠️ Volontairement dans localStorage et ⛔ PAS dans l'URL : un aperçu qui
// disparaît au rechargement fait croire que les toggles n'ont pas pris.
const CLE_APERCU = "omnihub-apercu-tenant";

const lireApercu = () => {
  try {
    return localStorage.getItem(CLE_APERCU) || null;
  } catch {
    return null;   // ⛔ navigation privée, stockage bloqué : on n'échoue pas
  }
};

export function PermissionProvider({ children, user, currentTenant }) {
  const [apercuTenant, setApercuTenant] = useState(lireApercu);
  const [permissionContext, setPermissionContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Flags BRUTS du tenant actif — lus seulement pour un super-admin hors aperçu (cf. featureExacte).
  const [flagsBruts, setFlagsBruts] = useState(null);

  // Charger le contexte d'autorisation depuis le backend
  const loadPermissionContext = useCallback(async () => {
    // Extraire le tenant ID
    const tenantId = currentTenant?.slug || currentTenant?.id || currentTenant;
    
    if (!user?.email || !tenantId) {
      console.log("[PermissionContext] Pas de user ou tenant, skip load", { email: user?.email, tenantId });
      setPermissionContext(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log("[PermissionContext] Chargement permissions pour", { email: user.email, tenantId });
      
      const token = localStorage.getItem('session_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await fetch(
        `${API}/api/platform/context?user_email=${encodeURIComponent(user.email)}&tenant_id=${encodeURIComponent(tenantId)}`
        + (apercuTenant && apercuTenant === String(tenantId) ? "&apercu=true" : ""),
        { headers, credentials: "include" }
      );
      
      if (response.ok) {
        const data = await response.json();
        console.log("[PermissionContext] Permissions chargées:", { hubs: Object.keys(data.hubs || {}), role: data.role });
        setPermissionContext(data);
        setError(null);
      } else {
        console.error("[PermissionContext] Erreur chargement:", response.status);
        setError("Impossible de charger les permissions");
      }
    } catch (err) {
      console.error("[PermissionContext] Erreur:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
    // ⚠️ `apercuTenant` DOIT figurer ici : sans lui, activer ou quitter
    // l'aperçu ne relancerait pas le chargement et l'écran ne bougerait pas.
  }, [user?.email, currentTenant, apercuTenant]);

  // Recharger quand user ou tenant change
  useEffect(() => {
    loadPermissionContext();
  }, [loadPermissionContext]);

  const tenantCourant = currentTenant?.slug || currentTenant?.id || currentTenant;
  const besoinFlagsBruts = Boolean(permissionContext?.is_super_admin && !permissionContext?.apercu && tenantCourant);
  useEffect(() => {
    if (!besoinFlagsBruts) {
      setFlagsBruts(null);
      return undefined;
    }
    let annule = false;
    const token = localStorage.getItem("session_token");
    fetch(`${API}/api/platform/features/${encodeURIComponent(tenantCourant)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: "include",
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!annule) setFlagsBruts(d && d.features ? { tenant: String(tenantCourant), flags: d.features } : null); })
      .catch(() => { if (!annule) setFlagsBruts(null); });
    return () => { annule = true; };
  }, [besoinFlagsBruts, tenantCourant]);

  // Titre d'onglet = "OmniHub - {TENANT}" (+ " - Preprod" en preprod).
  // Tenant DÉRIVÉ du contexte (currentTenant), env DÉRIVÉ du hostname — JAMAIS en dur :
  // un host sans "preprod" (prod) n'ajoute pas le suffixe → sûr à la promotion.
  // Se met à jour à chaque switch de tenant (currentTenant en dépendance). Avant
  // connexion (currentTenant null) → "OmniHub" (+ " - Preprod" en preprod).
  useEffect(() => {
    const slug =
      currentTenant?.slug ||
      currentTenant?.id ||
      (typeof currentTenant === "string" ? currentTenant : "");
    // Libellé court dérivé du slug : "omv_france" → "OMV", "tef" → "TEF".
    const tenantLabel = slug ? String(slug).split(/[_-]/)[0].toUpperCase() : "";
    const isPreprod = /preprod/i.test(window.location.hostname);
    let title = "OmniHub";
    if (tenantLabel) title += ` - ${tenantLabel}`;
    if (isPreprod) title += " - Preprod";
    document.title = title;
  }, [currentTenant]);

  // Écouter les changements de feature flags depuis RBACManager
  useEffect(() => {
    const handleFlagChange = () => {
      console.log("[PermissionContext] Feature flags changés, rechargement...");
      loadPermissionContext();
    };
    
    window.addEventListener('feature-flags-changed', handleFlagChange);
    return () => window.removeEventListener('feature-flags-changed', handleFlagChange);
  }, [loadPermissionContext]);

  // =========================================================================
  // HELPERS - Vérification des permissions
  // =========================================================================

  /**
   * Vérifie si un hub est activé (Feature Flag)
   */
  const isHubEnabled = useCallback((hubId) => {
  // ══ UNKNOWN ≠ ENABLED — DEC-079, applique ICI le 2026-09-10 ═══════════════
  //
  // 🔴 CE QUI ETAIT FAUX, mesure le 2026-09-09 : trois defauts differents
  // coexistaient dans ce fichier — hub « present et pas false », module
  // « present » (seul correct), capability « pas false ». Une cle ABSENTE
  // rendait donc ON. C'est ce qui a failli faire ouvrir `operation.visites`
  // en croyant aligner preprod sur prod.
  //
  // ⚠️ ET LE PIRE ETAIT LE CHARGEMENT : `if (!permissionContext) return true`
  // montrait TOUT avant de masquer. Un menu complet s'affichait une fraction
  // de seconde, puis se reduisait. On ⛔ ne montre plus rien qu'on n'a pas
  // encore le droit de montrer : pendant le chargement, la reponse est NON.
  // Le provider expose `loading` pour que les ecrans disent « chargement »
  // plutot que « vide ».
  //
  // 🔑 Desormais : seule une autorisation EXPLICITEMENT `true` ouvre.
    if (!permissionContext) return false;   // ⛔ chargement : on ne montre RIEN
    
    // Super Admin voit tout
    if (permissionContext.is_super_admin) return true;
    
    // Vérifier dans les hubs actifs
    const hubs = permissionContext.hubs || {};
    // ⛔ `=== true` et non `!== false` : `enabled` est TOUJOURS pose par
    // `_get_active_hubs` (verifie le 10/09), donc son absence est une anomalie,
    // ⛔ pas une autorisation.
    return hubs[hubId]?.enabled === true;
  }, [permissionContext]);

  /**
   * Vérifie si un module est activé (Feature Flag spécifique)
   */
  const isModuleEnabled = useCallback((hubId, moduleId) => {
    if (!permissionContext) return false;   // ⛔ chargement : NON
    if (permissionContext.is_super_admin) return true;
    
    // Vérifier d'abord que le hub est activé
    if (!isHubEnabled(hubId)) return false;
    
    // Vérifier dans les modules du hub
    const hubs = permissionContext.hubs || {};
    const hub = hubs[hubId];
    if (!hub) return false;
    
    const modules = hub.modules || {};
    return moduleId in modules;
  }, [permissionContext, isHubEnabled]);

  /**
   * Vérifie si l'utilisateur a une permission spécifique
   */
  const hasPermission = useCallback((hubId, moduleId, action) => {
    if (!permissionContext) return false;  // ⛔ chargement : NON
    
    // Super Admin a toutes les permissions
    if (permissionContext.is_super_admin) return true;
    
    // Admin tenant a toutes les permissions sur son tenant
    if (permissionContext.permissions === "all" || permissionContext.permissions === "all_tenant") {
      return true;
    }
    
    // Vérifier d'abord que le hub et module sont activés
    if (!isHubEnabled(hubId) || !isModuleEnabled(hubId, moduleId)) {
      return false;
    }
    
    // Vérifier la permission spécifique
    const permissions = permissionContext.permissions || {};
    if (typeof permissions !== "object") return false;
    
    const hubPerms = permissions[hubId] || {};
    const modulePerms = hubPerms[moduleId] || [];
    
    return modulePerms.includes(action);
  }, [permissionContext, isHubEnabled, isModuleEnabled]);

  /**
   * Vérifie si une feature flag est activée
   */
  const isFeatureEnabled = useCallback((featureId) => {
    if (!permissionContext) return false;   // ⛔ chargement : NON
    if (permissionContext.is_super_admin) return true;
    
    const flags = permissionContext.feature_flags || {};
    // 🔴 UNKNOWN ≠ ENABLED : une capability ABSENTE est FERMEE. Les 6 tenants
    // de production portent un document explicite sur les 96 capabilities
    // (complete le 10/09), donc aucune n'est absente par accident.
    return flags[featureId] === true;
  }, [permissionContext]);

  /**
   * T-351b — « peut ouvrir le Bureau AMO » : verdict SERVEUR (rôle admin ET module bureau_amo du tenant, comme les routes du Bureau).
   * Fail-closed : contexte non chargé ou clé absente = false.
   */
  const peutOuvrirBureau = useCallback(() => verdictBureau(permissionContext), [permissionContext]);
  /** T-406 — « ce compte peut ouvrir le Bureau de Chloé / de Joey » : même verdict serveur (`acces.<module>`), fail-closed (seul `true` ouvre). */
  const peutOuvrirAcces = useCallback((cle) => permissionContext?.acces?.[cle] === true, [permissionContext]);

  /**
   * Variante STRICTE : seule une clé explicitement à true ouvre (absente ou contexte non chargé = fermé).
   */
  const isFeatureGranted = useCallback(
    (featureId) => featureAccordee(permissionContext, featureId),
    [permissionContext]
  );

  /**
   * Variante EXACTE : comme isFeatureGranted mais SANS contournement super-admin (choix métier du tenant) ; flags bruts du tenant actif pour un super-admin hors aperçu.
   */
  const isFeatureExact = useCallback(
    (featureId) => featureExacte(
      permissionContext, featureId,
      flagsBruts && flagsBruts.tenant === String(tenantCourant) ? flagsBruts.flags : null),
    [permissionContext, flagsBruts, tenantCourant]
  );

  /**
   * Retourne les hubs actifs (pour filtrer la navigation)
   */
  const getActiveHubs = useCallback(() => {
    if (!permissionContext) return [];
    
    const hubs = permissionContext.hubs || {};
    return Object.keys(hubs);
  }, [permissionContext]);

  /**
   * Retourne le rôle de l'utilisateur
   */
  const getUserRole = useCallback(() => {
    // Le backend renvoie `role` (sans accent) — lire `.rôle` rendait TOUJOURS "user".
    return permissionContext?.role || "user";
  }, [permissionContext]);

  /**
   * Vérifie si l'utilisateur est Super Admin
   */
  const isSuperAdmin = useCallback(() => {
    return permissionContext?.is_super_admin || false;
  }, [permissionContext]);

  /**
   * Vérifie si l'utilisateur est Admin (tenant ou super)
   */
  const isAdmin = useCallback(() => {
    const role = permissionContext?.role;
    return role === "super_admin" || role === "admin" || permissionContext?.is_super_admin;
  }, [permissionContext]);

  // ══ APERÇU — entrée et SORTIE CLAIRE ════════════════════════════════════
  const activerApercu = useCallback((slug) => {
    const cible = String(slug || currentTenant?.slug || currentTenant || "");
    if (!cible) return;
    try { localStorage.setItem(CLE_APERCU, cible); } catch { /* stockage bloqué */ }
    setApercuTenant(cible);
  }, [currentTenant]);

  const quitterApercu = useCallback(() => {
    try { localStorage.removeItem(CLE_APERCU); } catch { /* stockage bloqué */ }
    setApercuTenant(null);
  }, []);

  // ⚠️ L'aperçu est ACTIF seulement si le serveur l'a CONFIRMÉ. On ⛔ ne se fie
  // pas au seul état local : si la route ignorait le paramètre, le bandeau
  // mentirait et on croirait voir Mario en voyant tout.
  const apercuActif = Boolean(permissionContext?.apercu);

  const value = {
    // État
    permissionContext,
    loading,
    error,
    apercuActif,
    apercuTenant,
    activerApercu,
    quitterApercu,
    
    // Méthodes de vérification
    isHubEnabled,
    isModuleEnabled,
    peutOuvrirBureau,
    peutOuvrirAcces,
    hasPermission,
    isFeatureEnabled,
    isFeatureGranted,
    isFeatureExact,
    getActiveHubs,
    getUserRole,
    isSuperAdmin,
    isAdmin,
    
    // Actions
    reload: loadPermissionContext
  };

  return (
    <PermissionContext.Provider value={value}>
      {/* 🔑 SORTIE CLAIRE — le bandeau est rendu par le provider lui-même, donc
          présent sur TOUS les écrans : on ⛔ ne peut pas rester en aperçu sans
          le savoir, ni chercher où en sortir. */}
      {apercuActif && (
        <div
          role="status"
          style={{
            position: "sticky", top: 0, zIndex: 60,
            display: "flex", alignItems: "center", justifyContent: "center",
            gap: 12, padding: "8px 16px",
            background: "var(--oh-stat-amber-bg)",
            color: "var(--oh-stat-amber-text)",
            borderBottom: "0.5px solid var(--oh-border)",
            fontSize: 13, lineHeight: 1.6,
          }}
        >
          <span>
            Aperçu de l'espace <strong>{apercuTenant}</strong> — vous voyez ce que
            voient ses utilisateurs. Vos droits d'administration restent actifs.
          </span>
          <button
            type="button"
            onClick={quitterApercu}
            data-testid="quitter-apercu"
            style={{
              padding: "4px 12px", borderRadius: 8,
              border: "0.5px solid var(--oh-accent)",
              background: "transparent", color: "var(--oh-accent-text)",
              cursor: "pointer", fontSize: 13,
            }}
          >
            Quitter l'aperçu
          </button>
        </div>
      )}
      {children}
    </PermissionContext.Provider>
  );
}

/**
 * Hook pour utiliser les permissions
 */
export function usePermissions() {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error("usePermissions must be used within a PermissionProvider");
  }
  return context;
}

/**
 * Hook simplifié pour vérifier une permission spécifique
 * Usage: const canTransition = useCanDo("commercial", "dossiers", "transition")
 */
export function useCanDo(hub, module, action) {
  const { hasPermission, loading } = usePermissions();
  
  if (loading) return false;  // ⛔ chargement : on ne montre RIEN
  return hasPermission(hub, module, action);
}

/**
 * Hook pour vérifier si un hub est visible
 * Usage: const showCommercial = useHubVisible("commercial")
 */
export function useHubVisible(hubId) {
  const { isHubEnabled, loading } = usePermissions();
  
  if (loading) return true;
  return isHubEnabled(hubId);
}

export default PermissionContext;
